import {
  ButtonBase,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDate } from '@utils/helpers';
import moment from 'moment';
import 'moment/locale/ja';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomPickerDay from './CustomPickerDate';
import './DateRangePicker.scss';
import { CloseSharpIcon, EventIcon } from '@assets/icons';

interface DateRangePickerProps {
  id?: string;
  label?: string;
  required?: boolean;
  startDate?: string;
  endDate?: string;
  isRow?: boolean;
  placeholder?: string;
  onChange: (start: string, end: string) => void;
  isShowClear?: boolean;
}

const AppDateRangePicker: FC<DateRangePickerProps> = ({
  id,
  label,
  required,
  startDate,
  endDate,
  isRow = false,
  placeholder,
  onChange,
  isShowClear,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<DateRangeValue>({
    start: startDate ? moment(startDate) : null,
    end: endDate ? moment(endDate) : null,
  });
  const [tempValue, setTempValue] = useState(value);

  const valueText = useMemo(() => {
    let text = '';
    if (value.start) {
      text += formatDate(value.start);
      if (value.end) {
        text += ' ~ ' + formatDate(value.end);
      }
    }
    return text;
  }, [value]);

  const handleSelect = (date: moment.Moment) => {
    if (!value.start || date < value.start || (value.start && value.end)) {
      value.start = date;
      value.end = null;
    } else {
      value.end = date;
      setTempValue({ ...value });
      setOpen(false);
      onChange?.(formatDate(value.start), formatDate(value.end));
    }
    setValue({ ...value });
  };

  const handleClose = () => {
    if (!value.end) {
      setValue({ ...tempValue });
    }
    setOpen(false);
  };

  const onClear = () => {
    value.start = null;
    value.end = null;
    setValue({ ...value });
    setTempValue({ ...value });
    onChange('', '');
  };

  return (
    <Grid
      container
      className="DateRangePicker"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems="center"
          justifyContent={
            isShowClear ? 'space-between' : isRow ? 'flex-end' : 'flex-start'
          }
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} className="DataPickerInput__Wrapper">
        <ButtonBase
          className="DateRangePickerCalendar"
          disableTouchRipple
          disabled={open}
          onClick={() => setOpen(true)}
        >
          <DatePicker
            closeOnSelect={false}
            open={open}
            views={['year', 'month', 'day']}
            slotProps={{
              textField: {
                className: valueText ? 'MuiFormControl-filled' : '',
                placeholder: valueText || t(placeholder!),
                value: null,
                InputProps: {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      className="CancelIconButton__Wrapper"
                    >
                      {!valueText && <EventIcon />}
                    </InputAdornment>
                  ),
                },
              },
              toolbar: { hidden: true },
              actionBar: { actions: [] },
            }}
            slots={{
              day: (pickerProps) => (
                <CustomPickerDay
                  {...pickerProps}
                  range={value}
                  onClick={() => handleSelect(pickerProps.day)}
                />
              ),
            }}
            onClose={handleClose}
          />
        </ButtonBase>
        {isShowClear && valueText && (
          <IconButton disabled={open} onClick={onClear} type="reset" edge="end">
            <CloseSharpIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

export default AppDateRangePicker;
